/**
 * ChatGPT Review-Paket — Operator-Confirmation-Bridge (30.09.2026)
 *
 * Fallback fuer den Fall, dass OpenAI produktive ChatGPT-MCP-Writes
 * (correct_document_metadata/finalize_case_review/decide_scan_review)
 * client-seitig blockiert: ChatGPT liefert ein strukturiertes JSON-
 * Review-Paket, der eingeloggte Olympus-Benutzer bestaetigt es hier EINMAL,
 * Olympus fuehrt danach ausschliesslich seine bestehenden, serverseitig
 * validierten Fachaktionen aus (backend/services/scan_review_v4.py) ueber
 * die bereits vorhandene Bridge aus.
 *
 * Diese Seite enthaelt KEINE eigene Fachlogik — nur:
 *   POST /api/scan-review/operator-bridge/validate  (read-only)
 *   POST /api/scan-review/operator-bridge/apply      (nur nach Bestaetigung)
 * Der Confirmation-Token wird ausschliesslich im React-State gehalten (kein
 * localStorage) — ein Seiten-Reload verwirft ihn automatisch.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Loader2, ShieldCheck } from 'lucide-react';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_META = {
  READY: { label: 'READY', icon: CheckCircle2, klass: 'bg-green-100 text-green-800 border-green-300' },
  NEEDS_HUMAN: { label: 'NEEDS_HUMAN', icon: HelpCircle, klass: 'bg-amber-100 text-amber-800 border-amber-300' },
  STALE: { label: 'STALE', icon: AlertTriangle, klass: 'bg-orange-100 text-orange-800 border-orange-300' },
  INVALID: { label: 'INVALID', icon: XCircle, klass: 'bg-red-100 text-red-800 border-red-300' },
  BLOCKED: { label: 'BLOCKED', icon: XCircle, klass: 'bg-red-100 text-red-800 border-red-300' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, icon: HelpCircle, klass: 'bg-gray-100 text-gray-800 border-gray-300' };
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={meta.klass}>
      <Icon className="w-3 h-3 mr-1" />{meta.label}
    </Badge>
  );
}

export default function ScanReviewOperatorBridgePage() {
  const [jsonText, setJsonText] = useState('');
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState(null); // { schema_version, items, counts, confirmation_token, payload_hash }
  const [validatedPayload, setValidatedPayload] = useState(null); // exakt der Payload, der validiert wurde
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const resetNachAenderung = () => {
    // Jede Textaenderung entwertet eine vorherige Validierung -- Apply darf
    // NIE gegen ein anderes Paket laufen als das gerade validierte.
    setValidateResult(null);
    setValidatedPayload(null);
    setApplyResult(null);
  };

  const handleValidate = async () => {
    let payload;
    try {
      payload = JSON.parse(jsonText);
    } catch (e) {
      toast.error('Ungültiges JSON: ' + e.message);
      return;
    }
    setValidating(true);
    setApplyResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/scan-review/operator-bridge/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Validierung fehlgeschlagen');
        setValidateResult(null);
        setValidatedPayload(null);
        return;
      }
      setValidateResult(data);
      setValidatedPayload(payload);
      toast.success(`Validiert: ${data.counts.READY} READY von ${data.items.length} Items`);
    } catch (e) {
      toast.error('Fehler bei der Validierung: ' + e.message);
    } finally {
      setValidating(false);
    }
  };

  const handleApply = async () => {
    if (!validateResult || !validatedPayload) return;
    setShowConfirm(false);
    setApplying(true);
    try {
      const res = await fetch(`${API_BASE}/api/scan-review/operator-bridge/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmation_token: validateResult.confirmation_token,
          payload: validatedPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Apply fehlgeschlagen');
        return;
      }
      setApplyResult(data);
      // Token verwerfen -- ist ohnehin serverseitig verbraucht (single-use).
      setValidateResult(null);
      setValidatedPayload(null);
      toast.success(`${data.anzahl_abgeschlossen} Review(s) abgeschlossen`);
    } catch (e) {
      toast.error('Fehler beim Apply: ' + e.message);
    } finally {
      setApplying(false);
    }
  };

  const counts = validateResult?.counts;
  const readyCount = counts?.READY || 0;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" /> ChatGPT Review-Paket (Operator-Confirmation-Bridge)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          ChatGPT liefert ein strukturiertes JSON-Paket aus der fachlichen Scan-Review-Prüfung.
          Hier einfügen, validieren, prüfen — erst nach ausdrücklicher Bestätigung wendet Olympus
          die bereits bestehenden Fachaktionen (Completion Gate inklusive) an.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">1. Paket einfügen</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); resetNachAenderung(); }}
            placeholder='{"schema_version": 1, "items": [...]}'
            className="font-mono text-xs min-h-[220px]"
          />
          <Button onClick={handleValidate} disabled={validating || !jsonText.trim()}>
            {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Paket validieren
          </Button>
        </CardContent>
      </Card>

      {validateResult && (
        <Card>
          <CardHeader><CardTitle className="text-base">2. Validierungsergebnis</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3 text-sm">
              <span>Gesamt: <strong>{validateResult.items.length}</strong></span>
              <span className="text-green-700">READY: <strong>{counts.READY}</strong></span>
              <span className="text-amber-700">NEEDS_HUMAN: <strong>{counts.NEEDS_HUMAN}</strong></span>
              <span className="text-orange-700">STALE: <strong>{counts.STALE}</strong></span>
              <span className="text-red-700">INVALID: <strong>{counts.INVALID}</strong></span>
              <span className="text-red-700">BLOCKED: <strong>{counts.BLOCKED}</strong></span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="p-2">Status</th>
                    <th className="p-2">Review</th>
                    <th className="p-2">Fall</th>
                    <th className="p-2">Dokument</th>
                    <th className="p-2">vorgesehene Aktion</th>
                    <th className="p-2">bestehende Aufgabe</th>
                    <th className="p-2">Frist</th>
                    <th className="p-2">Begründung</th>
                  </tr>
                </thead>
                <tbody>
                  {validateResult.items.map((it, i) => (
                    <tr key={i} className="border-b last:border-0 align-top">
                      <td className="p-2"><StatusBadge status={it.status} /></td>
                      <td className="p-2 font-mono text-xs">{it.review_id}</td>
                      <td className="p-2 font-mono text-xs">{it.expected_klient_id}</td>
                      <td className="p-2 font-mono text-xs">{it.dokument_id}</td>
                      <td className="p-2">{it.vorgesehene_aktion || '—'}</td>
                      <td className="p-2 font-mono text-xs">{it.bestehende_aufgabe || '—'}</td>
                      <td className="p-2">{it.frist || '—'}</td>
                      <td className="p-2 text-xs text-muted-foreground">{it.grund || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {readyCount > 0 ? (
              <Button variant="default" onClick={() => setShowConfirm(true)} disabled={applying}>
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Validierte Reviews jetzt anwenden ({readyCount})
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Kein Item ist READY — nichts zum Anwenden. NEEDS_HUMAN/STALE/INVALID/BLOCKED bleiben offen.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {showConfirm && (
        <Card className="border-primary">
          <CardHeader><CardTitle className="text-base">Bestätigung erforderlich</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p>
              Du bist dabei, <strong>{readyCount}</strong> validierte Scan-Review(s) in Olympus anzuwenden.
              Nur READY-Einträge werden verarbeitet. NEEDS_HUMAN, STALE und INVALID bleiben offen.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Abbrechen</Button>
              <Button variant="default" onClick={handleApply}>{readyCount} Review(s) jetzt anwenden</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {applyResult && (
        <Card>
          <CardHeader><CardTitle className="text-base">3. Apply-Ergebnis</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-green-700">APPLIED: <strong>{applyResult.anzahl_abgeschlossen}</strong></span>
              <span className="text-red-700">FEHLGESCHLAGEN: <strong>{applyResult.anzahl_fehlgeschlagen}</strong></span>
              {applyResult.replay && <Badge variant="outline">Wiederholter Aufruf — bereits vorher angewendet (idempotent, kein erneuter Write)</Badge>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="p-2">Review</th>
                    <th className="p-2">Angewandt</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Aufgabe</th>
                    <th className="p-2">Fehler/Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {(applyResult.items || []).map((it, i) => (
                    <tr key={i} className="border-b last:border-0 align-top">
                      <td className="p-2 font-mono text-xs">{it.review_id}</td>
                      <td className="p-2">{it.angewandt ? '✅' : '❌'}</td>
                      <td className="p-2">{it.status}</td>
                      <td className="p-2 font-mono text-xs">{it.aufgabe_id || '—'}</td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {typeof it.grund === 'string' ? it.grund : (it.grund ? JSON.stringify(it.grund) : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
